/**
 * API сервис для работы с KLS (Классификаторы и Справочники)
 * 
 * Реализует методы для всех основных сущностей:
 * - QualType (Типы квалификаторов)
 * - Qual (Квалификаторы/Справочники)
 * - Kls (Разделы классификаторов)
 * - KlsExternalSources (Внешние источники)
 * - KlsSyncLogs (Логи синхронизации)
 */

import { get, post, put, del, postFormData, downloadFile } from "@/lib/api-client-v2";
import type {
  QualType,
  QualTypeCreate,
  QualTypeUpdate,
  Qual,
  QualCreate,
  QualUpdate,
  QualWithType,
  Kls,
  KlsCreate,
  KlsUpdate,
  KlsWithQual,
  GeometryPrimitivesForSemanticResponse,
  KlsIcon,
  KlsHierarchyItem,
  KlsSearchResult,
  KlsMoveRequest,
  KlsMoveResponse,
  KlsExternalSources,
  KlsExternalSourcesCreate,
  KlsExternalSourcesUpdate,
  KlsSyncLogs,
  TestConnectionResponse,
  SyncResponse,
  ImportPreviewResponse,
  ImportResponse,
  ImportFormat,
  ExportFormat,
  EntityType,
  DuplicateStrategy,
} from "@/types/api-v2";

// ============================================================================
// QualType API (Типы квалификаторов)
// ============================================================================

export const qualTypesApi = {
  /**
   * Получить список типов квалификаторов
   */
  list: (params?: { skip?: number; limit?: number; include_deleted?: boolean }) =>
    get<QualType[]>("/kls/qual-types", params),

  /**
   * Получить тип квалификатора по ID
   */
  get: (qualTypeId: number) =>
    get<QualType>(`/kls/qual-types/${qualTypeId}`),

  /**
   * Создать тип квалификатора
   */
  create: (data: QualTypeCreate) =>
    post<QualType>("/kls/qual-types", data),

  /**
   * Обновить тип квалификатора
   */
  update: (qualTypeId: number, data: QualTypeUpdate) =>
    put<QualType>(`/kls/qual-types/${qualTypeId}`, data),

  /**
   * Удалить тип квалификатора (soft delete)
   */
  delete: (qualTypeId: number) =>
    del(`/kls/qual-types/${qualTypeId}`),
};

// ============================================================================
// Qual API (Квалификаторы/Справочники)
// ============================================================================

export const qualsApi = {
  /**
   * Получить список квалификаторов
   */
  list: (params?: { 
    skip?: number; 
    limit?: number; 
    qual_type_id?: number;
    include_deleted?: boolean;
  }) =>
    get<QualWithType[]>("/kls/quals", params),

  /**
   * Получить квалификатор по ID
   */
  get: (qualId: number) =>
    get<QualWithType>(`/kls/quals/${qualId}`),

  /**
   * Создать квалификатор
   */
  create: (data: QualCreate) =>
    post<Qual>("/kls/quals", data),

  /**
   * Обновить квалификатор
   */
  update: (qualId: number, data: QualUpdate) =>
    put<Qual>(`/kls/quals/${qualId}`, data),

  /**
   * Удалить квалификатор (soft delete)
   */
  delete: (qualId: number) =>
    del(`/kls/quals/${qualId}`),

  /**
   * Получить количество разделов в квалификаторе
   */
  getKlsCount: (qualId: number) =>
    get<{ count: number }>(`/kls/quals/${qualId}/kls-count`),
};

// ============================================================================
// Kls API (Разделы классификаторов)
// ============================================================================

export const klsSectionsApi = {
  /**
   * Получить список разделов классификаторов
   */
  list: (params?: { 
    qual_id?: number;
    skip?: number; 
    limit?: number;
    include_deleted?: boolean;
  }) =>
    get<KlsWithQual[]>("/kls/kls", params),

  /**
   * Получить раздел классификатора по ID
   */
  get: (klsId: number) =>
    get<KlsWithQual>(`/kls/kls/${klsId}`),

  /**
   * Создать раздел классификатора
   */
  create: (data: KlsCreate) =>
    post<Kls>("/kls/kls", data),

  /**
   * Обновить раздел классификатора
   */
  update: (klsId: number, data: KlsUpdate) =>
    put<Kls>(`/kls/kls/${klsId}`, data),

  /**
   * Удалить раздел классификатора (soft delete)
   */
  delete: (klsId: number) =>
    del(`/kls/kls/${klsId}`),

  /**
   * Поиск разделов классификаторов
   */
  search: (params: {
    q: string;
    qual_id?: number;
    limit?: number;
  }) =>
    get<KlsSearchResult[]>("/kls/kls/search", params),

  /**
   * Получить иерархию разделов
   */
  getHierarchy: (qualId: number) =>
    get<KlsHierarchyItem[]>(`/kls/kls/hierarchy/${qualId}`),

  /**
   * Переместить узел в иерархии
   */
  move: (klsId: number, data: KlsMoveRequest) =>
    post<KlsMoveResponse>(`/kls/kls/${klsId}/move`, data),

  /**
   * Получить иконки разделов по квалификатору
   */
  icons: (params: { qual_id: number; include_deleted?: boolean }) =>
    get<KlsIcon[]>("/kls/kls/icons", params),

  /**
   * Допустимые примитивы геометрии (qual 343) для семантического типа (kls_kls).
   */
  geometryPrimitivesForSemantic: (semanticKlsId: number) =>
    get<GeometryPrimitivesForSemanticResponse>(
      `/kls/kls/geometry-primitives-for-semantic/${semanticKlsId}`,
    ),
};

// ============================================================================
// KlsExternalSources API (Внешние источники)
// ============================================================================

export const klsExternalSourcesApi = {
  /**
   * Получить список внешних источников
   */
  list: (params?: { 
    skip?: number; 
    limit?: number;
    is_active?: boolean;
  }) =>
    get<KlsExternalSources[]>("/kls/sources", params),

  /**
   * Получить внешний источник по ID
   */
  get: (sourceId: number) =>
    get<KlsExternalSources>(`/kls/sources/${sourceId}`),

  /**
   * Создать внешний источник
   */
  create: (data: KlsExternalSourcesCreate) =>
    post<KlsExternalSources>("/kls/sources", data),

  /**
   * Обновить внешний источник
   */
  update: (sourceId: number, data: KlsExternalSourcesUpdate) =>
    put<KlsExternalSources>(`/kls/sources/${sourceId}`, data),

  /**
   * Удалить внешний источник
   */
  delete: (sourceId: number) =>
    del(`/kls/sources/${sourceId}`),

  /**
   * Тестировать подключение к источнику
   */
  testConnection: (sourceId: number) =>
    post<TestConnectionResponse>(`/kls/sources/${sourceId}/test-connection`),

  /**
   * Запустить синхронизацию из источника
   */
  sync: (sourceId: number, params?: { dry_run?: boolean }) =>
    post<SyncResponse>(`/kls/sources/${sourceId}/sync`, params),
};

// ============================================================================
// KlsSyncLogs API (Логи синхронизации)
// ============================================================================

export const klsSyncLogsApi = {
  /**
   * Получить список логов синхронизации
   */
  list: (params?: { 
    skip?: number; 
    limit?: number;
    source_id?: number;
    status?: string;
  }) =>
    get<KlsSyncLogs[]>("/kls/sync-logs", params),

  /**
   * Получить лог синхронизации по ID
   */
  get: (logId: number) =>
    get<KlsSyncLogs>(`/kls/sync-logs/${logId}`),

  /**
   * Получить последние ошибки синхронизации
   */
  getFailures: (params?: { limit?: number }) =>
    get<KlsSyncLogs[]>("/kls/sync-logs/failures", params),
};

// ============================================================================
// Импорт/Экспорт API
// ============================================================================

export const klsImportExportApi = {
  /**
   * Предпросмотр импортируемых данных
   */
  previewImport: (
    file: File,
    format: ImportFormat,
    entityType: EntityType,
    mapping?: Record<string, string>
  ): Promise<ImportPreviewResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("format", format);
    formData.append("entity_type", entityType);
    if (mapping) {
      formData.append("mapping", JSON.stringify(mapping));
    }
    return postFormData<ImportPreviewResponse>("/kls/import/preview", formData);
  },

  /**
   * Импорт данных из файла
   */
  import: (
    file: File,
    format: ImportFormat,
    entityType: EntityType,
    duplicateStrategy: DuplicateStrategy = "error",
    mapping?: Record<string, string>
  ): Promise<ImportResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("format", format);
    formData.append("entity_type", entityType);
    formData.append("duplicate_strategy", duplicateStrategy);
    if (mapping) {
      formData.append("mapping", JSON.stringify(mapping));
    }
    return postFormData<ImportResponse>("/kls/import", formData);
  },

  /**
   * Экспорт данных в файл
   */
  export: (
    format: ExportFormat,
    entityType: EntityType,
    params?: {
      qual_type_id?: number;
      qual_id?: number;
      include_deleted?: boolean;
      date_from?: string;
      date_to?: string;
    }
  ): Promise<Blob> => {
    const queryParams: Record<string, string> = {
      format,
      entity_type: entityType,
    };
    if (params?.qual_type_id) {
      queryParams.qual_type_id = String(params.qual_type_id);
    }
    if (params?.qual_id) {
      queryParams.qual_id = String(params.qual_id);
    }
    if (params?.include_deleted !== undefined) {
      queryParams.include_deleted = String(params.include_deleted);
    }
    if (params?.date_from) {
      queryParams.date_from = params.date_from;
    }
    if (params?.date_to) {
      queryParams.date_to = params.date_to;
    }
    return downloadFile("/kls/export", queryParams);
  },

  /**
   * Скачать шаблон для импорта
   */
  downloadTemplate: (
    format: ExportFormat,
    entityType: EntityType
  ): Promise<Blob> => {
    return downloadFile("/kls/export/template", {
      format,
      entity_type: entityType,
    });
  },
};

// ============================================================================
// Общий экспорт
// ============================================================================

export const klsApi = {
  qualTypes: qualTypesApi,
  quals: qualsApi,
  kls: klsSectionsApi,
  externalSources: klsExternalSourcesApi,
  syncLogs: klsSyncLogsApi,
  importExport: klsImportExportApi,
};

